import React, { useState, useEffect, useCallback } from "react";
import styles from "../../pages/tablesStyle/RevisionSection.module.css";
import WarehouseModal from "./customComponentsTable/WarehouseModal";
import CreateRevisionModal from "../../customComponents/CreateRevisionModal";
import CustomSelect from "../../customComponents/CustomSelect";
import RevisionWorkspace from "../../customComponents/RevisionWorkspace";
import { API_URL } from "../../config/api";

const RevisionSection = () => {
  const [revisions, setRevisions] = useState([]);
  const [storages, setStorages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isWorkspaceOpen, setIsWorkspaceOpen] = useState(false);

  const [selectedStorage, setSelectedStorage] = useState("");
  const [type, setType] = useState("");

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isWarehouseModalOpen, setIsWarehouseModalOpen] = useState(false);

  const [newRevisionName, setNewRevisionName] = useState("");
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [createdRevision, setCreatedRevision] = useState(null);

  const token = localStorage.getItem("token"); // ✅ токен

  const statusTranslations = {
    approved: "Одобрено",
    pending: "В ожидании",
    rejected: "Отклонено",
    draft: "Черновик",
  };

  // ✅ Загрузка складов (useCallback для ESLint deps)
  const fetchStorages = useCallback(async () => {
    if (!token) return console.error("❌ Нет токена!");
    try {
      const res = await fetch(`${API_URL}/api/storages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (Array.isArray(data.data)) {
        setStorages(data.data);
        localStorage.setItem("storages", JSON.stringify(data.data));
      } else {
        throw new Error("Ожидался массив складов");
      }
    } catch (err) {
      console.error("Ошибка при загрузке складов:", err);
      const saved = localStorage.getItem("storages");
      if (saved) setStorages(JSON.parse(saved));
    }
  }, [token]);

  // ✅ Загрузка ревизий (useCallback для ESLint deps)
  const fetchRevisions = useCallback(
    async (storageId = "") => {
      if (!token) return console.error("❌ Нет токена!");
      setLoading(true);
      try {
        const url = storageId
          ? `${API_URL}/api/stock_changes?type=revision&storage_id=${storageId}`
          : `${API_URL}/api/stock_changes?type=revision`;

        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();

        if (Array.isArray(data.data)) {
          setRevisions(data.data);
          localStorage.setItem("revisions", JSON.stringify(data.data));
        } else {
          setRevisions([]);
        }
      } catch (err) {
        console.error("Ошибка при загрузке ревизий:", err);
        const saved = localStorage.getItem("revisions");
        if (saved) setRevisions(JSON.parse(saved));
        else setRevisions([]);
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  // ✅ ESLint fix: добавили fetchStorages/fetchRevisions в deps
  useEffect(() => {
    fetchStorages();
    fetchRevisions();
  }, [token, fetchStorages, fetchRevisions]);

  const handleStorageChange = (storageName) => {
    setSelectedStorage(storageName);
    const selected = storages.find((s) => s.name === storageName);
    fetchRevisions(selected?.id);
  };

  const handleCreateRevision = (name) => {
    setNewRevisionName(name);
    setIsCreateModalOpen(false);
    setIsWarehouseModalOpen(true);
  };

  const handleWarehouseSelect = (warehouse) => {
    setSelectedWarehouse(warehouse);
    setIsWarehouseModalOpen(false);
    setCreatedRevision({
      name: newRevisionName,
      warehouseId: warehouse.id,
      warehouseName: warehouse.name,
    });
    setIsWorkspaceOpen(true);
  };

  return (
    <div className={styles.container}>
      {/* Кнопка + */}
      <button className={styles.addSuppliers} onClick={() => setIsCreateModalOpen(true)}>
        +
      </button>

      <div className={styles.filter}>
        <CustomSelect
          className={styles.filters__select}
          options={["Select 1", "Select 2", "Select 3", "Select 4"]}
          value={type}
          onChange={setType}
          placeholder="Select"
        />

        <input type="date" className={styles.input} />

        <CustomSelect
          className={styles.filters__select}
          options={storages.map((s) => s.name)}
          value={selectedStorage}
          onChange={handleStorageChange}
          placeholder="Выберите склад"
        />

        <input type="text" placeholder="Название" className={styles.input} />
        <input type="text" placeholder="Номер документа" className={styles.input} />

        <CustomSelect
          className={styles.filters__select}
          options={["Select 1", "Select 2", "Select 3", "Select 4"]}
          value={type}
          onChange={setType}
          placeholder="Select"
        />
      </div>

      <CreateRevisionModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onContinue={handleCreateRevision}
      />

      <WarehouseModal
        isOpen={isWarehouseModalOpen}
        onClose={() => setIsWarehouseModalOpen(false)}
        onSelect={handleWarehouseSelect}
      />

      {isWorkspaceOpen && (
        <RevisionWorkspace
          revision={createdRevision}
          onClose={() => setIsWorkspaceOpen(false)}
          onRevisionFinished={() => {
            setIsWorkspaceOpen(false);
            fetchRevisions(selectedWarehouse?.id || "");
          }}
        />
      )}

      {/* --- Основная таблица --- */}
      <div className={styles.tableWrapper}>
        {loading ? (
          <p>Загрузка...</p>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Название</th>
                <th>Номер документа</th>
                <th>Дата</th>
                <th>Сотрудник</th>
                <th>Статус</th>
                <th>Синхронизация</th>
                <th>Склад</th>
              </tr>
            </thead>
            <tbody>
              {revisions.length === 0 ? (
                <tr>
                  <td colSpan="7">Нет данных</td>
                </tr>
              ) : (
                revisions.map((rev) => (
                  <tr key={rev.id}>
                    <td
                      className={styles.clickable}
                      onClick={() => {
                        setCreatedRevision({ ...rev, flag: true });
                        setIsWorkspaceOpen(true);
                      }}
                    >
                      {rev.name || "—"}
                    </td>
                    <td>{rev.id || "—"}</td>
                    <td>
                      {rev.inserted_at ? new Date(rev.inserted_at).toLocaleDateString() : "—"}
                    </td>
                    <td>{rev.employee_name || "—"}</td>
                    <td>{statusTranslations[rev.status] || rev.status || "—"}</td>
                    <td>{rev.sync_status || "—"}</td>
                    <td>
                      {storages.find((s) => s.id === rev.storage_id)?.name || `ID: ${rev.storage_id}`}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default RevisionSection;
